<script>
  import { progress, DAILY_GOAL } from "../lib/state.svelte.js";
  import { CURRICULUM } from "../../data/problems/index.js";
  import { TRACKS, CATEGORIES } from "../../data/taxonomy.js";
  import {
    myRating,
    ratingInfo,
    rankProgress,
    solvedCount,
    trackProgress,
    categoryProgress,
    weakCategories,
    todayCount,
    BADGES,
    pickRandomProblem,
  } from "../lib/economy.js";
  import { eraForEvo } from "../../data/journeyEngine.js";
  import { fmtEra, fmtFuture, fmtDuration, today, varColor } from "../lib/format.js";
  import { openProblem, go, nav } from "../lib/nav.svelte.js";
  import { canUseMissed, missedProblems, startDrill } from "../lib/missed.svelte.js";

  const rating = $derived(myRating());
  const rank = $derived(ratingInfo(rating));
  const rp = $derived(rankProgress());
  const here = $derived(eraForEvo(progress.evo));
  const done = $derived(todayCount());
  const weak = $derived(weakCategories(3));
  const missed = $derived(canUseMissed() ? missedProblems() : []);

  function drillMissed() {
    const id = startDrill(null);
    if (id) openProblem(id, "dash");
  }
  const totalProblems = CURRICULUM.length;

  let radarEl = $state(null);
  let ringEl = $state(null);

  /** devicePixelRatio に合わせて実解像度を上げる */
  function prep(cv, cssW, cssH) {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    cv.width = Math.floor(cssW * dpr);
    cv.height = Math.floor(cssH * dpr);
    cv.style.height = cssH + "px";
    const ctx = cv.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);
    return ctx;
  }

  function drawRadar() {
    if (!radarEl) return;
    const W = radarEl.clientWidth || 320;
    const H = 250;
    const ctx = prep(radarEl, W, H);

    const cx = W / 2;
    const cy = H / 2 + 6;
    const R = Math.min(W, H) * 0.32;
    const axes = TRACKS;
    const n = axes.length;
    const line = varColor("--line", "#2c4b41");
    const sub = varColor("--sub", "#9dbfb2");
    const accent = varColor("--accent", "#4fd1a5");

    // 目盛りの多角形
    ctx.strokeStyle = line;
    ctx.lineWidth = 1;
    for (let ring = 1; ring <= 4; ring++) {
      ctx.beginPath();
      const r = (R * ring) / 4;
      for (let i = 0; i <= n; i++) {
        const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
        const x = cx + r * Math.cos(a);
        const y = cy + r * Math.sin(a);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.stroke();
    }

    // 軸
    ctx.beginPath();
    for (let i = 0; i < n; i++) {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + R * Math.cos(a), cy + R * Math.sin(a));
    }
    ctx.stroke();

    // ラベル
    ctx.font = "12px system-ui, sans-serif";
    axes.forEach((t, i) => {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
      const lx = cx + (R + 22) * Math.cos(a);
      const ly = cy + (R + 14) * Math.sin(a);
      ctx.textAlign = i === 0 || i === 2 ? "center" : i === 1 ? "left" : "right";
      ctx.fillStyle = t.color;
      ctx.fillText(t.short, lx, ly + 4);
    });

    // データ
    const vals = axes.map((t) => trackProgress(t.id).ratio);
    ctx.beginPath();
    axes.forEach((t, i) => {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
      const r = R * Math.max(0.02, vals[i]);
      const x = cx + r * Math.cos(a);
      const y = cy + r * Math.sin(a);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.fillStyle = "rgba(79,209,165,0.22)";
    ctx.fill();
    ctx.strokeStyle = accent;
    ctx.lineWidth = 2;
    ctx.stroke();

    axes.forEach((t, i) => {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
      const r = R * Math.max(0.02, vals[i]);
      ctx.beginPath();
      ctx.arc(cx + r * Math.cos(a), cy + r * Math.sin(a), 4, 0, Math.PI * 2);
      ctx.fillStyle = t.color;
      ctx.fill();
    });

    if (solvedCount() === 0) {
      ctx.fillStyle = sub;
      ctx.textAlign = "center";
      ctx.font = "12px system-ui, sans-serif";
      ctx.fillText("問題を解くと分野ごとの習熟度が出ます", cx, H - 8);
    }
  }

  function drawRing() {
    if (!ringEl) return;
    const S = 128;
    const ctx = prep(ringEl, S, S);
    const p = Math.min(1, done / DAILY_GOAL);
    const cx = S / 2;
    const cy = S / 2;
    const r = 50;

    ctx.lineWidth = 11;
    ctx.strokeStyle = varColor("--panel2", "#1b322b");
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();

    if (p > 0) {
      ctx.strokeStyle = p >= 1 ? varColor("--accent2", "#7bd8ff") : varColor("--accent", "#4fd1a5");
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * p);
      ctx.stroke();
    }

    ctx.fillStyle = varColor("--txt", "#e8f5ef");
    ctx.textAlign = "center";
    ctx.font = "700 22px system-ui, sans-serif";
    ctx.fillText(`${done}/${DAILY_GOAL}`, cx, cy + 4);
    ctx.fillStyle = varColor("--sub", "#9dbfb2");
    ctx.font = "11px system-ui, sans-serif";
    ctx.fillText(p >= 1 ? "達成！" : "今日の目標", cx, cy + 22);
  }

  $effect(() => {
    // progress を読んで依存を張る
    void progress.evo;
    void progress.activity[today()];
    void Object.keys(progress.solved).length;
    drawRadar();
    drawRing();
  });

  $effect(() => {
    const onResize = () => {
      drawRadar();
      drawRing();
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  });

  /** GitHub 風の学習カレンダー（列＝週、行＝曜日） */
  const calendar = $derived.by(() => {
    void progress.activity;
    const cols = 21;
    // 最終列に今日が入るよう、「今週の日曜」から (cols-1) 週ぶんさかのぼる。
    // 単純に cols*7 日前から数えると、最後の列が今日より手前で終わってしまう。
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - d.getDay()); // 今週の日曜
    d.setDate(d.getDate() - (cols - 1) * 7);

    const t = today();
    const out = [];
    for (let c = 0; c < cols; c++) {
      const col = [];
      for (let r = 0; r < 7; r++) {
        const ymd =
          d.getFullYear() +
          "-" +
          String(d.getMonth() + 1).padStart(2, "0") +
          "-" +
          String(d.getDate()).padStart(2, "0");
        const count = progress.activity[ymd] || 0;
        const level = count >= 5 ? 4 : count >= 3 ? 3 : count >= 2 ? 2 : count >= 1 ? 1 : 0;
        col.push({ ymd, count, level, isToday: ymd === t });
        d.setDate(d.getDate() + 1);
      }
      out.push(col);
    }
    return out;
  });

  function jumpTo(catId) {
    const cat = CATEGORIES.find((c) => c.id === catId);
    if (!cat) return;
    const unsolved = CURRICULUM.filter((p) => p.category === catId && !progress.solved[p.id]);
    if (unsolved.length > 0) {
      openProblem(unsolved[0].id, "dash");
    } else {
      nav.trackId = cat.track;
      go("learn");
    }
  }

  function surprise() {
    const id = pickRandomProblem();
    if (id) openProblem(id, "dash");
  }
</script>

<section>
  <div class="dash-top">
    <div class="card" style="margin:0">
      <div class="stat-label">実力ランク</div>
      <div class="stat-hero" style="color:{rank.color}">{rank.em} {rank.name}</div>
      <div class="tiny" style="margin-top:2px">{rank.desc}</div>
      <div class="bar"><i style="width:{rp.percent * 100}%"></i></div>
      <div class="tiny" style="margin-top:6px">
        {#if rp.next}
          レート {rating} ／ 次は「{rp.next.name}」まであと {rp.remaining} 点
        {:else}
          レート {rating} ／ 最高ランクに到達しました！
        {/if}
      </div>
      <div style="margin-top:10px"><span class="pos-chip">{rank.pos}</span></div>
    </div>

    <div class="card" style="margin:0">
      <div class="stat-label">旅の現在地</div>
      <div class="stat-hero">
        {here.era.em}
        {here.isFuture ? fmtFuture(here.futureMa) : fmtEra(here.ma)}
      </div>
      <div class="tiny" style="margin-top:2px">{here.era.name} ／ {here.era.desc}</div>
      <div class="tagrow" style="margin-top:10px">
        <span class="tag">✅ {solvedCount()} / {totalProblems} 問</span>
        <span class="tag">🔥 連続 {progress.streak} 日</span>
        <span class="tag">⚡ 最高コンボ {progress.bestCombo}</span>
        <span class="tag">⏱ 累計 {fmtDuration(progress.solveTimeTotal)}</span>
      </div>
    </div>
  </div>

  <div class="two-col">
    <div class="card">
      <h3>📊 分野別の習熟度</h3>
      <canvas class="chart" bind:this={radarEl}></canvas>
    </div>

    <div class="card">
      <h3>🎯 今日のミッション</h3>
      <div style="display:flex; gap:16px; align-items:center; flex-wrap:wrap">
        <canvas bind:this={ringEl} style="width:128px; flex:0 0 auto"></canvas>
        <div style="flex:1; min-width:150px">
          <p class="tiny" style="margin-top:0">
            1日に {DAILY_GOAL} 問正解すると、ボーナスの進化ポイントがもらえます。
          </p>
          <button class="btn submit" onclick={surprise}>🎲 いま1問やる</button>
        </div>
      </div>
    </div>
  </div>

  <div class="card">
    <h3>🗂️ カテゴリ別の進み具合</h3>
    <div class="cat-bars">
      {#each CATEGORIES as c (c.id)}
        {@const p = categoryProgress(c.id)}
        <div class="cat-bar-row">
          <span class="name">{c.em} {c.name}</span>
          <span class="num">{p.solved}/{p.total}</span>
          <div class="bar thin"><i style="width:{p.ratio * 100}%"></i></div>
        </div>
      {/each}
    </div>
  </div>

  {#if weak.length > 0 || missed.length > 0}
    <div class="card">
      <h3>💡 次におすすめ</h3>
      <div class="reco-list">
        {#if missed.length > 0}
          <button class="reco miss-reco" onclick={drillMissed}>
            <span style="font-size:20px">🎯</span>
            <span style="flex:1">
              <div style="font-weight:600">間違えた問題を解き直す</div>
              <div class="tiny">{missed.length} 問が未克服 · 正解すると一覧から外れます</div>
            </span>
            <span class="evo-tag">▶</span>
          </button>
        {/if}
        {#each weak as w (w.id)}
          <button class="reco" onclick={() => jumpTo(w.id)}>
            <span style="font-size:20px">{w.em}</span>
            <span style="flex:1">
              <div style="font-weight:600">{w.name}</div>
              <div class="tiny">{w.solved} / {w.total} 問 · あと {w.total - w.solved} 問</div>
            </span>
            <span class="evo-tag">→</span>
          </button>
        {/each}
      </div>
    </div>
  {/if}

  <div class="card">
    <h3>🌱 学習カレンダー</h3>
    <div class="cal-grid">
      {#each calendar as col, ci (ci)}
        <div class="cal-col">
          {#each col as cell (cell.ymd)}
            <div
              class="cal-cell l{cell.level}"
              class:today={cell.isToday}
              title="{cell.ymd}：{cell.count}問"
            ></div>
          {/each}
        </div>
      {/each}
    </div>
    <div class="cal-legend">
      <span class="tiny">少</span>
      <div class="cal-cell l0"></div>
      <div class="cal-cell l1"></div>
      <div class="cal-cell l2"></div>
      <div class="cal-cell l3"></div>
      <div class="cal-cell l4"></div>
      <span class="tiny">多</span>
    </div>
  </div>

  <div class="card">
    <h3>🏅 バッジ（{progress.badges.length} / {BADGES.length}）</h3>
    <div class="badge-list">
      {#each BADGES as b (b.id)}
        {@const owned = progress.badges.includes(b.id)}
        <div class="badge" class:locked={!owned}>
          <span class="badge-em">{owned ? b.em : "🔒"}</span>
          <span>
            <div style="font-weight:700">{b.name}</div>
            <div class="tiny">{b.desc}</div>
          </span>
        </div>
      {/each}
    </div>
  </div>
</section>
